import { Component, ReactNode } from 'react'
import { Button, Heading, Text } from '@agion/ui'
import { AlertTriangle } from 'lucide-react'
import i18n from '@/app/i18n'

interface Props {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  reset = () => {
    this.setState({ hasError: false, error: null })
  }

  override render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset)
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-[var(--background)]">
          <div className="max-w-md text-center">
            <div className="mx-auto mb-4 h-12 w-12 text-red-500 dark:text-red-400">
              <AlertTriangle className="h-12 w-12" />
            </div>
            <Heading level="h2" as="h1" className="mb-2">
              {i18n.t('common:errorBoundary.somethingWentWrong', 'Something went wrong')}
            </Heading>
            <Text color="muted" className="mb-4">
              {this.state.error.message || i18n.t('common:errorBoundary.unexpectedError', 'An unexpected error occurred')}
            </Text>
            <div className="space-x-2">
              <Button
                onClick={this.reset}
                variant="primary"
                size="sm"
              >
                {i18n.t('common:errorBoundary.tryAgain', 'Try Again')}
              </Button>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                size="sm"
              >
                {i18n.t('common:errorBoundary.reloadPage', 'Reload Page')}
              </Button>
            </div>
            {import.meta.env.MODE === 'development' && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-[var(--muted-foreground)]">
                  {i18n.t('common:errorBoundary.errorDetails', 'Error details (Development)')}</summary>
                <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-red-600 dark:text-red-400">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}